import { useCallback, useEffect, useRef, useState } from "react";

export interface InfinitePageResult<T> {
  list: T[];
  total?: number;
}

/**
 * 作品列表无限滚动：首次 loadFirstPage 拉第 1 页，底部 sentinel 进入视口后续拉。
 * 追加时按 id 去重，避免生成/轮询插入后分页窗口错位造成重复。
 */
export function useInfinitePagedList<T>(options: {
  pageSize: number;
  getId: (item: T) => string | number;
  enabled?: boolean;
}) {
  const { pageSize, getId, enabled = true } = options;
  const [items, setItems] = useState<T[]>([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const seqRef = useRef(0);
  const inflightRef = useRef(false);
  const hasMoreRef = useRef(false);
  const pageNoRef = useRef(1);
  const getIdRef = useRef(getId);
  const fetchRef = useRef<((pageNo: number) => Promise<InfinitePageResult<T>>) | null>(null);
  getIdRef.current = getId;

  const runFetch = useCallback(async (
    page: number,
    mode: "replace" | "append",
    fetchPage: (pageNo: number) => Promise<InfinitePageResult<T>>,
  ): Promise<T[]> => {
    if (mode === "append") {
      if (inflightRef.current || !hasMoreRef.current) return [];
    } else {
      seqRef.current += 1;
      hasMoreRef.current = false;
    }
    const seq = seqRef.current;
    inflightRef.current = true;
    if (mode === "append") setLoadingMore(true);
    try {
      const res = await fetchPage(page);
      if (seq !== seqRef.current) return [];
      const list = Array.isArray(res.list) ? res.list : [];
      if (typeof res.total === "number") setTotal(res.total);
      pageNoRef.current = page;
      setItems((prev) => {
        if (mode === "replace") return list;
        const seen = new Set(prev.map((item) => getIdRef.current(item)));
        return [...prev, ...list.filter((item) => !seen.has(getIdRef.current(item)))];
      });
      const more = list.length === 0
        ? false
        : typeof res.total === "number"
          ? page * pageSize < res.total
          : list.length >= pageSize;
      hasMoreRef.current = more;
      setHasMore(more);
      return list;
    } catch {
      if (seq !== seqRef.current) return [];
      return [];
    } finally {
      if (seq === seqRef.current) {
        inflightRef.current = false;
        setLoadingMore(false);
      }
    }
  }, [pageSize]);

  const loadFirstPage = useCallback(async (
    fetchPage: (pageNo: number) => Promise<InfinitePageResult<T>>,
  ): Promise<T[]> => {
    fetchRef.current = fetchPage;
    return runFetch(1, "replace", fetchPage);
  }, [runFetch]);

  const loadNextPage = useCallback(() => {
    const fetchPage = fetchRef.current;
    if (!fetchPage) return;
    void runFetch(pageNoRef.current + 1, "append", fetchPage);
  }, [runFetch]);

  useEffect(() => {
    if (!enabled || !hasMore) return;
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadNextPage();
      },
      { rootMargin: "280px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [enabled, hasMore, loadNextPage, items.length]);

  return {
    items,
    setItems,
    total,
    setTotal,
    hasMore,
    loadingMore,
    sentinelRef,
    loadFirstPage,
  };
}
